<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';

const props = defineProps<{
  revisionUid?: string | null;
  page?: number | null;
  highlights?: string[];
}>();

const emit = defineEmits<{
  /** Fired when the user has dwelled on a page long enough to count as a visit. */
  pageVisit: [page: number];
}>();

const pdfRef = ref<InstanceType<typeof GaiaVaultFilePdfPreview>>();
const loaded = ref(false);

// A page counts as visited after a short dwell, so rapid next-clicking
// through a document doesn't report every page. One emit per page per file.
const VISIT_DWELL_MS = 1000;
let visitTimer: ReturnType<typeof setTimeout> | null = null;
const visitedPages = new Set<number>();

function cancelPendingVisit(): void {
  if (visitTimer !== null) {
    clearTimeout(visitTimer);
    visitTimer = null;
  }
}

function scheduleVisit(page: number): void {
  cancelPendingVisit();
  if (visitedPages.has(page)) return;
  visitTimer = setTimeout(() => {
    visitTimer = null;
    visitedPages.add(page);
    emit('pageVisit', page);
  }, VISIT_DWELL_MS);
}

onBeforeUnmount(cancelPendingVisit);

const highlightTexts = computed<string[]>(() =>
  (props.highlights ?? []).filter(text => text.trim().length > 0)
);

function applyPage(): void {
  if (props.page && props.page >= 1) {
    pdfRef.value?.goToPage(props.page);
  }
}

function applyHighlights(): void {
  const pdf = pdfRef.value;
  if (!pdf) return;
  if (highlightTexts.value.length > 0) {
    // Delay so the PDF text layer of the target page is rendered before matching.
    const texts = highlightTexts.value;
    setTimeout(() => {
      void pdfRef.value?.setHighlightText(texts);
    }, 500);
  } else {
    pdf.clearHighlights();
  }
}

function onLoaded(totalPages: number): void {
  loaded.value = true;
  applyPage();
  applyHighlights();
  // A valid deep-link jump reports itself via page-change; otherwise the
  // viewer sits on page 1 without ever emitting.
  if (!(props.page && props.page >= 1 && props.page <= totalPages)) {
    scheduleVisit(1);
  }
}

watch(
  () => props.revisionUid,
  () => {
    loaded.value = false;
    cancelPendingVisit();
    visitedPages.clear();
  }
);

watch(
  () => props.page,
  () => {
    if (loaded.value) applyPage();
  }
);

watch(highlightTexts, () => {
  if (loaded.value) applyHighlights();
});
</script>

<template>
  <gaia-vault-file-pdf-preview
    v-if="revisionUid"
    ref="pdfRef"
    :key="revisionUid"
    :revision-uid="revisionUid"
    @loaded="onLoaded"
    @page-change="scheduleVisit"
  />
  <n-empty v-else description="No file selected" style="margin-top: 48px" />
</template>
